import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { GameCard } from '../components/GameCard';
import { Select } from '../components/Select';
import { BannerHead, BannerSteps, PageBanner } from '../components/PageBanner';
import { SectionHead } from '../components/SectionHead';
import { SkeletonGrid } from '../components/Skeleton';
import { api, isFinal, type Game, type League } from '../lib/api';
import { byImportance } from '../lib/importance';
import { useAllGames } from '../lib/useGames';

export type Feature = 'predict' | 'betting' | 'breakdown';

const COPY: Record<Feature, { eyebrow: string; title: string; sub: string; steps: [string, string][]; tab: string; finals: boolean }> = {
  predict: {
    eyebrow: 'Predict', title: 'Who wins, and by how much.', sub: 'Pick a game. Kova weighs ten seasons of matchups, the current line, injuries, weather, and the most similar past games, then shows its work.',
    steps: [['01', 'Gathers history, injuries, weather, and the line'], ['02', 'Finds the most similar past games'], ['03', 'Calls a winner, a margin, and a win probability']], tab: 'predict', finals: false,
  },
  betting: {
    eyebrow: 'Betting coach', title: 'Where the market is wrong.', sub: 'Kova prices every moneyline, spread, and total against its own probability, strips the vig, and only calls an edge when it clears a floor.',
    steps: [['01', 'Reads the current market and its movement'], ['02', 'Prices each side with the model'], ['03', 'Reports edge, expected value, and a stake']], tab: 'betting', finals: false,
  },
  breakdown: {
    eyebrow: 'Breakdown', title: 'Why the game went the way it did.', sub: 'For any finished game, Kova finds the primary cause, the turning point, and what people will blame that was not actually the reason.',
    steps: [['01', 'Reads the play-by-play and win probability'], ['02', 'Finds the swing that decided it'], ['03', 'Separates causes from noise']], tab: 'predict', finals: true,
  },
};

const THIS_SEASON = new Date().getFullYear();
const SEASONS = Array.from({ length: THIS_SEASON - 2015 }, (_, i) => THIS_SEASON - i);

export function FeaturePage({ feature }: { feature: Feature }) {
  const c = COPY[feature];
  const { games, loading } = useAllGames();
  const [league, setLeague] = useState<League | 'all'>('all');

  // Breaking down a game means picking one that has been played, and the live
  // scoreboard only ever holds the current week, so mid-week this page said
  // "nothing to show right now". Any week of any season is fetchable and a
  // finished game never changes, so it is cached rather than polled.
  const [season, setSeason] = useState<number | null>(null);
  const [week, setWeek] = useState(1);
  const [past, setPast] = useState<Game[] | null>(null);
  const browsing = c.finals && season !== null;

  useEffect(() => {
    if (!browsing) {
      // Not browsing a past season: show the games most recently played, since the
      // scoreboard holds next week's once the current one is over.
      if (!c.finals) { setPast(null); return; }
      let live = true;
      const leagues: League[] = league === 'all' ? ['nfl', 'cfb'] : [league];
      Promise.all(leagues.map((lg) => api.recentGames(lg).catch(() => [])))
        .then((all) => live && setPast(all.flat().filter(isFinal)));
      return () => { live = false; };
    }
    let live = true;
    setPast(null);
    const leagues: League[] = league === 'all' ? ['nfl', 'cfb'] : [league];
    Promise.all(leagues.map((lg) => api.pastGames(lg, season, week).then((r) => r.games).catch(() => [])))
      .then((all) => live && setPast(all.flat()));
    return () => { live = false; };
  }, [browsing, season, week, league]);

  const list = useMemo(() => {
    if (c.finals) return byImportance(past ?? []);
    const pool = games.filter((g) => (league === 'all' || (g.league ?? 'nfl') === league) && (c.finals ? isFinal(g) : !isFinal(g)));
    return byImportance(pool);
  }, [games, league, c.finals, browsing, past]);
  return (
    <>
      <PageBanner>
        <BannerHead eyebrow={c.eyebrow} title={c.title} sub={c.sub} aside={<BannerSteps steps={c.steps} />} />
      </PageBanner>
      <section className="section">
        <div className="container">
          <SectionHead title={c.finals ? 'Pick a finished game.' : 'Pick a game.'} eyebrow={c.finals ? 'Finals' : 'Upcoming'}
            action={
              <div className="flex flex-wrap items-center gap-2">
                {c.finals && (
                  <>
                    <Select label="Season" value={season ?? ''}
                            onChange={(v) => setSeason(v ? Number(v) : null)}
                            options={[['', 'This week'] as const, ...SEASONS.map((y) => [y, `${y} season`] as const)]} />
                    {season !== null && (
                      <Select label="Week" value={week} onChange={(v) => setWeek(Number(v))}
                              options={Array.from({ length: league === 'cfb' ? 15 : 22 }, (_, i) => i + 1)
                                .map((w) => [w, w > 18 ? `Playoff round ${w - 18}` : `Week ${w}`] as const)} />
                    )}
                  </>
                )}
                <div className="seg">{([['all', 'All'], ['nfl', 'NFL'], ['cfb', 'CFB']] as const).map(([id, label]) => <button key={id} onClick={() => setLeague(id)} className={`seg-item ${league === id ? 'seg-item-on' : ''}`}>{label}</button>)}</div>
              </div>
            } />
          {(loading || (browsing && past === null)) && !list.length ? <SkeletonGrid n={8} /> : list.length ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{list.slice(0, 16).map((g) => <GameCard key={g.id} g={g} tab={c.tab} run action={c.finals ? 'Break it down' : feature === 'betting' ? 'Find the edge' : 'Predict'} />)}</div>
          ) : (
            <p className="body-muted">
              {c.finals
                ? 'No finished games there. Pick another week, or a past season.'
                : <>Nothing to show right now. <Link to="/" className="text-blue">Back home</Link>.</>}
            </p>
          )}
        </div>
      </section>
    </>
  );
}
